import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, BookOpen, Settings, BarChart3 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { usePet } from "@/hooks/usePet";
import { useTypedTranslation } from "@/i18n/useTypedTranslation";
import { localePath } from "@/i18n/localePath";

const NAV_ITEMS_DEFAULT = [
  { key: "chat", icon: MessageCircle, labelKey: "mobile_nav.chat", path: "/chat" },
  { key: "journal", icon: BookOpen, labelKey: "mobile_nav.journal", path: "/journal" },
  { key: "settings", icon: Settings, labelKey: "mobile_nav.settings", path: "/settings" },
];

const NAV_ITEMS_KIDS = [
  { key: "chat", icon: MessageCircle, labelKey: "mobile_nav.chat", path: "/chat" },
  { key: "report", icon: BarChart3, labelKey: "mobile_nav.report", path: "/report" },
  { key: "journal", icon: BookOpen, labelKey: "mobile_nav.journal", path: "/journal" },
  { key: "settings", icon: Settings, labelKey: "mobile_nav.settings", path: "/settings" },
];

// Pages where bottom nav should NOT show
const HIDDEN_PATHS = ["/", "/auth", "/onboarding", "/garden", "/pet"];

export const MobileBottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { pet } = usePet();
  const { t, i18n } = useTypedTranslation();
  const lp = (path: string) => localePath(path, i18n.language);

  // Don't show if not logged in or no pet
  if (!user || !pet) return null;

  // Don't show on public/landing pages
  const isHidden = HIDDEN_PATHS.some((p) =>
    p === "/" ? location.pathname === "/" : location.pathname.startsWith(p)
  );
  if (isHidden) return null;

  const isKids = pet.mode === "kids";
  const items = isKids ? NAV_ITEMS_KIDS : NAV_ITEMS_DEFAULT;

  const getPath = (item: (typeof items)[0]) => {
    if (item.key === "report") return lp(`/report/${pet.id}`);
    return lp(item.path);
  };

  const isActive = (item: (typeof items)[0]) => {
    if (item.key === "report") return location.pathname.startsWith("/report") || location.pathname.includes("/report");
    return location.pathname === lp(item.path) || location.pathname === item.path;
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 sm:hidden"
      style={{
        background: "rgba(255,248,240,0.9)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderTop: "1px solid rgba(255,200,150,0.12)",
      }}
    >
      <div className="flex items-center justify-around h-14 max-w-lg mx-auto">
        {items.map((item) => {
          const active = isActive(item);
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              onClick={() => navigate(getPath(item))}
              className="flex flex-col items-center gap-0.5 py-1 px-3 min-w-0 relative"
              aria-label={t(item.labelKey as any)}
            >
              <Icon
                className="h-5 w-5 transition-colors"
                style={{
                  color: active
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted-foreground))",
                }}
              />
              <span
                className="text-[10px] font-medium transition-colors"
                style={{
                  color: active
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted-foreground))",
                  fontWeight: active ? 700 : 500,
                }}
              >
                {t(item.labelKey as any)}
              </span>
              {active && (
                <motion.div
                  layoutId="bottom-nav-indicator"
                  className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full bg-primary"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
            </button>
          );
        })}
      </div>
      {/* Safe area padding for iOS */}
      <div className="h-[env(safe-area-inset-bottom)]" />
    </nav>
  );
};
